import { BehaviorSubject, interval } from 'rxjs';
import { catchError, max} from 'rxjs/operators';
import { Injectable} from '@angular/core';
import { EventService } from './event.service';

import { TeacherProvider } from '../providers/teacher/teacher.provider'
import { Teacher } from '../models/teacher';
import { Course } from '../models/course';
import { Lesson } from '../models/lesson';

import { plainToClass } from 'class-transformer';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Base } from '../models/base-model';
import { ParentEducationMoment } from '../models/parent-education-moment';

import { CookieService } from 'ngx-cookie-service'
import { TeacherStore } from '../stores/teacher.store';
import { ResourcesProvider } from '../providers/resources/resources.provider';

import * as Sentry from '@sentry/browser';


@Injectable()
export class DataSyncService {

    private interval: any
    private someNonzeroNumberToIndicateDataHasSynced = 1

    private isSaving = new BehaviorSubject<boolean>(false);
    public _isSaving: Observable<boolean> = this.isSaving.asObservable();

    private isLoading = new BehaviorSubject<boolean>(false);
    public _isLoading: Observable<boolean> = this.isLoading.asObservable();

    private _saveCount: number = 0

    private _shouldCache: boolean = true
    
    constructor(private http: HttpClient,
        private eventService: EventService,
        private teacherProvider: TeacherProvider,
        private resourcesProvider: ResourcesProvider,
        private cookieService: CookieService,
        private teacherStore: TeacherStore) {

        this.eventService.dirtyTeacherDataSource$.subscribe((teacher: Teacher) => {
            if (teacher != null) {
                this.sync(teacher);
            }
        })
        const observable = new Observable<Teacher>( observer => {
            if (navigator && navigator.serviceWorker) {
                navigator.serviceWorker.ready.then(function(serviceWorkerRegistration) {
                    observer.next()
                });                
            }
        });
        observable.subscribe(() => {
            this.interval = interval(environment.pollingInterval)
            this.interval.subscribe(() => {
                const currentPath = window.location.pathname;
                const token = localStorage.getItem('token');

                // 1. If token, assume all is ok
                if (token && this.teacherProvider.teacher != null) {
                    if (currentPath !== '/login') {
                        this.sync(this.teacherProvider.teacher)

                        // If the resources are empty, then get them fresh from the server
                        this.resourcesProvider.getResources().subscribe(() => {
                            
                        })                        
                    }
                } else if (this.cookieService.check('MusicTogetherUser')) {
                    // 2. If no token but there is a cookie, check authorization
                    this.eventService.publishAuthorize();
                } else {
                    // 3. If no token or cookie, log out the user
                    if (currentPath !== '/login') {
                        this.eventService.publishLoggedOut();
                    }
                }
            });
        });

        this._shouldCache = false
        // if (navigator?.storage?.estimate) {
        //     try {
        //         navigator.storage.estimate().then((value: StorageEstimate) => {
        //             this._shouldCache = (value.usage < value.quota * .7)
        //         })                
        //     } catch (exc) {
        //         // Do nothing
        //     }
        // }

    }
    
    get isDataSaving(): any {
        return this._isSaving;
    }

    get isDataLoading(): any {
        return this._isLoading
    }

    private sync(teacher: Teacher) {

        if (teacher?.isSending) {
            return
        }

        // navigator.storage.estimate().then((value: StorageEstimate) => {
        //     this._shouldCache = (value.usage < value.quota * .7)
        // })

        if (teacher?.isDirty) {
            this._saveCount++
            if (this._saveCount == 1) {
                this.sendCourses(teacher)
                this.sendLessons(teacher)
                this.sendCustomPems(teacher)                        
            }
            this._saveCount--
        }
        if (this._saveCount <= 0) {
            this.loadTeacher(teacher)
            try {
                Sentry.setContext('teacher', {
                  name: teacher?.user?.name || '',
                  email: teacher?.user?.email || '',
                  uuid: teacher?.user?.uuid
                });        
              } catch (exc) {
        
              }
        }
    }

    private loadTeacher(teacher: Teacher) {
        if (teacher?.isDirty || teacher?.isSending) {
            return
        }
        if (this._saveCount > 0) {
            return
        }
        if (!navigator.onLine) {
            return
        }

        // this.isLoading.next(true)

        this.http.get('/teachers?hash=' + (teacher.hash || '') + '&' + Math.random(), {observe: 'response'})
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher, false, false)))
            .subscribe((r: HttpResponse<any>) => {
                if (r.body['status'] === 204) {
                    if (this._shouldCache) {
                        this.teacherProvider.cacheSongs()
                    }
                    // this.isLoading.next(false)
                    
                    // Success but the api returned an empty body since its hash matches the hash we sent.
                    return
                }

                if (!teacher.isDirty && !teacher.isSending) {
                    const updatedTeacher: Teacher = plainToClass(Teacher, r.body.data || Teacher.defaults()) as Teacher

                    if (this._saveCount <= 0) {
                        teacher.collectionUuids = updatedTeacher.collectionUuids
                        teacher.synced = updatedTeacher.synced
                        teacher.hash = updatedTeacher.hash
                        teacher.pages = updatedTeacher.pages
                        teacher.articles = updatedTeacher.articles
                        teacher.customPems = updatedTeacher.customPems

                        let maxSynced = teacher.getMaximumSyncedValue()
                        if (maxSynced <= updatedTeacher.synced) {
                            teacher.courses = updatedTeacher.courses
                        }

                        teacher.resetSendDate()
                        this.teacherStore.setTeacher(teacher).then();
                        if (this._shouldCache) {
                            this.teacherProvider.cacheSongs()
                        }
                    }
                }
                // this.isLoading.next(false)
            })
    }

    private sendCourses(teacher: Teacher): boolean {

        if (!navigator.onLine) {
            this.teacherStore.setTeacher(teacher).then()
            return false
        }

        for (const course of teacher.courses) {
            if (course.isDeleted) {
                if (course.isDirty) {
                    if (course.synced == null) { // never been written to server, so just delete locally
                        course.setSendDate()
                    } else {
                        this.preSend(teacher, course)
                        this.http.delete('/courses/' + course.uuid, { observe: 'response'})
                            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                            .subscribe((r: any) => {
                                this.postSend(teacher)
                            });
                        // return false
                    }
                }
            } else if (course.synced == null) { // Create course
                this.preSend(teacher, course)
                this.http.post('/courses', course, {observe: 'response'})
                    .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                    .subscribe((r: any) => {
                        course.synced = this.someNonzeroNumberToIndicateDataHasSynced
                        if (r.body.data) {
                            const c: Course = plainToClass(Course, r.body.data || {}) as any
                            course.synced = c.synced
                        }
                        this.postSend(teacher)
                    });
                // return false
            } else if (course.isDirty) { // Update course
                this.preSend(teacher, course)
                this.http.put('/courses/' + course.uuid, course, {observe: 'response'})
                    .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                    .subscribe((r: any) => {
                        if (r.body.data) {
                            const c: Course = plainToClass(Course, r.body.data || {}) as any
                            course.synced = c.synced
                        }
                        this.postSend(teacher)
                    });
                // return false
            }
        }

        return true
    }

    private sendLessons(teacher: Teacher): boolean {

        if (!navigator.onLine) {
            this.teacherStore.setTeacher(teacher).then()
            return false
        }

        for (const course of teacher.courses) {
            for (const lesson of course.lessons) {
                if (lesson.isDeleted) {
                    if (lesson.isDirty) {
                        if (lesson.synced == null) { // never been written to server, so just delete locally
                            course.setSendDate()
                        } else {
                            this.preSend(teacher, lesson)
                            this.http.delete('/lessons/' + lesson.uuid, { observe: 'response'})
                                .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                                .subscribe((r: any) => {
                                    this.postSend(teacher)
                                });
                            // return false
                        }
                    }
                } else if (lesson.synced == null) { // Create lesson
                    this.preSend(teacher, lesson)
                    const obj = Object.assign({courseUuid: course.uuid, order: course.indexOfLesson(lesson.uuid) + 1}, lesson);
                    this.http.post('/lessons', obj, {observe: 'response'})
                        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                        .subscribe((r: any) => {
                            if (r.body.data) {
                                // TODO: #465 Doug to fix r.body.data being null
                                const l: Lesson = plainToClass(Lesson, r.body.data || {}) as any
                                lesson.synced = l.synced

                                for (const lessonSong of lesson.songs) {
                                    const ls = l.findLessonSong(lessonSong.uuid)
                                    lessonSong.synced = ls.synced
                                }
                            } else {
                                lesson.synced = this.someNonzeroNumberToIndicateDataHasSynced
                                for (const lessonSong of lesson.songs) {
                                    lessonSong.synced = this.someNonzeroNumberToIndicateDataHasSynced
                                }
                            }

                            this.postSend(teacher)
                        });
                    // return false
                } else if (lesson.isDirty) { // Update lesson
                    this.preSend(teacher, lesson)
                    const obj = Object.assign({courseUuid: course.uuid, order: course.indexOfLesson(lesson.uuid) + 1}, lesson);
                    this.http.put('/lessons/' + lesson.uuid, obj, {observe: 'response'})
                        .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                        .subscribe((r: any) => {
                            if (r.body.data) {
                                const l: Lesson = plainToClass(Lesson, r.body.data || {}) as any
                                lesson.synced = l.synced

                                for (const lessonSong of lesson.songs) {
                                    const ls = l.findLessonSong(lessonSong.uuid)
                                    lessonSong.synced = ls.synced
                                }
                            }

                            this.postSend(teacher)
                        });
                    // return false
                }
            }
        }

        return true
    }

    private sendCustomPems(teacher: Teacher): boolean {

        if (!navigator.onLine) {
            this.teacherStore.setTeacher(teacher).then()
            return false
        }

        for (const pem of teacher.customPems) {
            if (pem.isDeleted) {
                if (pem.isDirty) {
                    // never been written to server, so just delete locally
                    if (pem.synced == null) { 
                        pem.setSendDate()
                    } else {
                        this.preSend(teacher, pem)
                        this.http.delete('/customPems/' + pem.uuid, { observe: 'response'})
                            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                            .subscribe((r: any) => {
                                this.postSend(teacher)
                            });
                        // return false
                    }
                }
            } else if (pem.synced == null) { // Create customPem
                this.preSend(teacher, pem)
                this.http.post('/customPems', pem, {observe: 'response'})
                    .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                    .subscribe((r: any) => {
                        if (r.body.data) {
                            const p: ParentEducationMoment = plainToClass(ParentEducationMoment, r.body.data || {}) as any
                            pem.synced = p.synced
                        } else {
                            pem.synced = this.someNonzeroNumberToIndicateDataHasSynced
                        }
                        this.postSend(teacher)
                    });
                // return false
            } else if (pem.isDirty) { // Update customPem

                this.preSend(teacher, pem)
                pem.setSendDate()
                this.http.put('/customPems/' + pem.uuid, pem, {observe: 'response'})
                    .pipe(catchError((error: HttpErrorResponse) => this.handleError(error, teacher)))
                    .subscribe((r: any) => {
                        if (r.body.data) {
                            const p: ParentEducationMoment = plainToClass(ParentEducationMoment, r.body.data || {}) as any

                            pem.synced = p.synced
                            pem.sendDate = p.synced
                        }
                        this.postSend(teacher)
                    });
                // return false
            }
        }

        return true
    }


    private preSend(teacher: Teacher, data: Base) {
        teacher.sendCount += 1
        data.setSendDate()
        this.isSaving.next(true);
    }

    private postSend(teacher: Teacher) {
        teacher.sendCount -= 1
        // if (teacher.sendCount < 0) {
        //     teacher.sendCount = 0
        // }
        this.isSaving.next(false);
        this.teacherStore.setTeacher(teacher).then()
    }

    private handleError(error: HttpErrorResponse, teacher: Teacher, decrementCounter: Boolean = true, persistTeacher: boolean = true) {
        if (decrementCounter) {
            teacher.sendCount -= 1
        }

        if (persistTeacher) {
            this.teacherStore.setTeacher(teacher).then()
        }
        this.eventService.publishApiError(error)

        return throwError(`${error.status} - ${error.message}`)
    }

}
